import {client} from '@/utils/gql'
import {NotFoundError, ServerError} from '@/utils/server/errors'

import {
  Brand_Subscription_Bool_Exp,
  Brand_Subscription_Insert_Input,
  Brand_Subscription_Order_By,
  Brand_Subscription_Select_Column,
  Brand_Subscription_Set_Input,
  BrandSubscriptionDetailsFragment,
  BrandSubscriptionFragment,
} from '../graphqlSdk'

const getByIdOrNull = async (id: string): Promise<BrandSubscriptionFragment | null> =>
  (await client.brandSubscriptionGetById({id})).brand_subscription_by_pk ?? null

const getById = async (id: string): Promise<BrandSubscriptionFragment> => {
  const response = await getByIdOrNull(id)

  if (!response) {
    throw new NotFoundError('Brand subscription by ID returned null', {id})
  }

  return response
}

const getDetailsByIdOrNull = async (id: string): Promise<BrandSubscriptionDetailsFragment | null> =>
  (await client.brandSubscriptionGetDetailsById({id})).brand_subscription_by_pk ?? null

const getMany = async (
  where: Brand_Subscription_Bool_Exp,
  order_by?: Brand_Subscription_Order_By,
  offset?: number,
  limit?: number,
  distinct_on?: Brand_Subscription_Select_Column,
): Promise<BrandSubscriptionFragment[]> =>
  (await client.brandSubscriptionGetMany({where, order_by, offset, limit, distinct_on}))
    .brand_subscription

const insertOne = async (
  object: Brand_Subscription_Insert_Input,
): Promise<BrandSubscriptionFragment> => {
  const brandSubscription = (await client.brandSubscriptionInsertOne({object}))
    .insert_brand_subscription_one

  if (!brandSubscription) {
    throw new ServerError('Brand subscription insert one results not returned', {
      object,
    })
  }

  return brandSubscription
}

const updateOne = async (
  id: string,
  changes: Brand_Subscription_Set_Input,
): Promise<BrandSubscriptionFragment> => {
  const brand = (await client.brandSubscriptionUpdateOne({id, changes}))
    .update_brand_subscription_by_pk

  if (!brand) {
    throw new ServerError('Brand subscription update returned null', {id, changes})
  }

  return brand
}

const updateMany = async (
  where: Brand_Subscription_Bool_Exp,
  set: Brand_Subscription_Set_Input,
): Promise<BrandSubscriptionFragment[]> => {
  const updates = (await client.brandSubscriptionUpdateMany({where, set})).update_brand_subscription

  if (!updates?.returning) {
    throw new ServerError('Brand subscription update many results not returned', {
      where,
      set,
      updates,
    })
  }

  return updates.returning
}

const count = async (filter: Brand_Subscription_Bool_Exp): Promise<number> => {
  const count = (await client.brandSubscriptionCount({filter})).brand_subscription_aggregate
    .aggregate?.count

  if (typeof count !== 'number') {
    throw new ServerError('Brand subscription count returned invalid value', {filter, count})
  }

  return count
}

const getOneOrNull = async (
  filter: Brand_Subscription_Bool_Exp,
): Promise<BrandSubscriptionFragment | null> => {
  const brandSubscriptions = (await client.brandSubscriptionGetMany({where: filter}))
    .brand_subscription

  if (brandSubscriptions.length > 1) {
    throw new ServerError('Get one returned multiple results', {filter})
  }

  return brandSubscriptions?.[0] ?? null
}

const getOne = async (filter: Brand_Subscription_Bool_Exp): Promise<BrandSubscriptionFragment> => {
  const brandSubscription = await getOneOrNull(filter)

  if (!brandSubscription) {
    throw new NotFoundError('Get one returned null', {filter})
  }

  return brandSubscription
}

export const brandSubscriptionOrm = {
  getByIdOrNull,
  getDetailsByIdOrNull,
  getById,
  getMany,
  insertOne,
  updateOne,
  updateMany,
  getOneOrNull,
  getOne,
  aggregate: {
    count,
  },
}
